import { track } from "../lib/metrics.js";
import { attributionName, deviceOwnerMap } from "../lib/adults.js";
import { coachEnabled } from "../lib/coachEnabled.js";
import { digestFor, digestRangeLabel, lastWeekBounds } from "../lib/weeklyDigest.js";
import { effectiveSpellingLists } from "../lib/spelling.js";
import { effectiveVocabCategories } from "../lib/vocab.js";
import { effectiveMathsBands } from "../lib/maths.js";
import { ActivityList, ActivityRow, Avatar, CoinIcon, Icon, VaultIcon, ChildMark } from "../components/Common.jsx";
import { apiActOnJoinRequest, apiGetDevices, apiGetJoinRequests, getDeviceId } from "../lib/api.js";
import { KidDetail } from "../components/KidDetail.jsx";
import { ParentProfileSwitcher } from "../components/Onboarding.jsx";
import { KidChips } from "../components/KidChips.jsx";
import { ProfilesTab } from "../components/ProfilesTab.jsx";
import { NewChildSetupSheet } from "../components/Wizard.jsx";
import { RequestsTab } from "../components/RequestsTab.jsx";
import { SettingsTab } from "../components/SettingsTab.jsx";
import { CURRENCY_SYMBOL } from "../lib/constants.js";
import { activityRowStyle, fmtActivityStamp, fmtGBP, fmtPence, todayStr, weekStartOf } from "../lib/utils.js";
import { clampApr } from "../lib/vaultInterest.js";
import { t as ct } from "../lib/content.js";
import { LearnEarnHub } from "./AdaptiveHub.jsx";
import { markReferralShared, shareReferral, showReferralHomeCard } from "../lib/referral.js";
import { useBackLayer } from "../lib/backstack.js";
const { useState, useEffect, useCallback, useMemo, useRef, useId } = React;

export function ParentView({ state, update, updateAndSave, activeChild, setActiveChildId, addTransaction, moveTokensToVault, reverseTransaction, reverseRequest, rejectVaultTransfer, showToast, parentTab, setParentTab, code, verifiedPin, setPendingVerifiedPin, setPendingVerifiedPasscode, setPendingVerifiedJoinToken, onLeaveHousehold, hiddenIds, onToggleVisibility, mutedIds, onToggleMute, unsavedGuardRef, onOpenSettings, onApproveHomework = () => {}, onRejectHomework = () => {}, requestsFocus = null, onRequestsFocusHandled = () => {}, onHomeBusyChange = () => {} }) {

  // LB-19 Zip B: the Learn & Earn hub (picker → Adaptive summary → game detail / Manual)
  // opened by the Home quick action. Kids-tab row keeps its direct Manual mount.
  const [leHubOpen, setLeHubOpen] = useState(false);
  // Set by Parent Home's Set homework tile: the child whose Set homework the hub opens on.
  const [leHubSetHomework, setLeHubSetHomework] = useState(null);
  useEffect(() => { if (parentTab !== "home" && leHubOpen) setLeHubOpen(false); }, [parentTab]); // eslint-disable-line react-hooks/exhaustive-deps
  // App's waiting-requests overlay never pops up over the hub (2 Oct): it asks whether Home is busy.
  useEffect(() => { onHomeBusyChange(leHubOpen); return () => onHomeBusyChange(false); }, [leHubOpen]); // eslint-disable-line react-hooks/exhaustive-deps

  // W2-PR(iii) — "via {device}" on transactions (review 3.6 display half): resolve the
  // registry once per mount. Suffix renders only when the household has >1 known device
  // — on a single-device household every row would say the same thing and the suffix
  // becomes noise (P5: dispute context, not surveillance theatre). "system" rows
  // (Money Monday accruals) always label, so automatic money movement is never
  // mistaken for a person's action.
  // LB-341 (J1/J2) — a device asking to join waits where everything else waits.
  //
  // This used to be polled only while the "Add a device" card was open in Settings, three
  // taps deep, which meant the adult who sent the link had to go looking for the thing they
  // were waiting for (Alan, 19 Sep: "I had to go looking for it deep in settings. Not
  // great."). Three channels carry it now: the push (worker), this list in Requests, and the
  // overlay below when one arrives with the app already open.
  //
  // Polled from Parent Mode as a whole, not from a card, and at 20s rather than the card's
  // 5s: this runs for as long as a parent is in the app, so the cost matters and nobody is
  // staring at it waiting for the tick.
  const [joinRequests, setJoinRequests] = useState([]);
  const [joinBusy, setJoinBusy] = useState(null);
  // Shown once per request id. A parent who dismisses the overlay has SEEN it; the row in
  // Requests is the record, and re-raising the sheet on the next tick would be nagging.
  const seenJoinRef = useRef(new Set());
  const [joinOverlay, setJoinOverlay] = useState(null);
  // True while a parent is sitting on Settings -> Add a device: the screen they just sent a
  // link from, and the one moment they are actually waiting on this. The poll below runs at
  // the rate that card's own poll used to, so folding two surfaces into one did not make the
  // wait longer than it was (Alan, 22 Sep).
  const [awaitingJoin, setAwaitingJoin] = useState(false);
  useEffect(() => {
    let live = true;
    let failures = 0;
    let id = null;
    const tick = async () => {
      const rs = await apiGetJoinRequests(code);
      if (!live) return;
      // null is "the call failed", not "there are none" — the same distinction the API
      // helper draws. Three in a row and we stop rather than retry a refusal all evening.
      if (rs === null) { failures += 1; if (failures >= 3 && id) clearInterval(id); return; }
      failures = 0;
      setJoinRequests(rs);
      const fresh = rs.find((r) => !seenJoinRef.current.has(r.requestId));
      if (fresh) { seenJoinRef.current.add(fresh.requestId); setJoinOverlay(fresh); }
    };
    // 20s is the resting rate. It was chosen for a poll that runs the whole time a parent is
    // in the app, but it also meant a parent who sent a link and is WAITING could sit for
    // twenty seconds after the other device asked (Alan, 20 Sep: it appeared after a pull to
    // refresh, and he could not tell whether the two were related — they were not).
    //
    // So: tick on focus as well. Coming back to the app, or switching to it from the phone
    // you just sent the link from, shows a waiting request at once. That is the moment a
    // parent is most likely to be looking, and it costs one request rather than a faster
    // interval running all evening.
    const onVisible = () => { if (document.visibilityState === "visible") tick(); };
    document.addEventListener("visibilitychange", onVisible);
    window.addEventListener("focus", onVisible);
    id = setInterval(tick, awaitingJoin ? 5000 : 20000);
    tick();
    return () => {
      live = false;
      if (id) clearInterval(id);
      document.removeEventListener("visibilitychange", onVisible);
      window.removeEventListener("focus", onVisible);
    };
  }, [code, awaitingJoin]);

  async function actOnJoinRequest(requestId, action) {
    setJoinBusy(requestId);
    const ok = await apiActOnJoinRequest(code, requestId, action, null);
    setJoinBusy(null);
    if (!ok) { showToast(ct("toast.join_action_failed", "Couldn't do that just now"), "bad"); return; }
    setJoinRequests((rs) => rs.filter((r) => r.requestId !== requestId));
    setJoinOverlay((o) => (o && o.requestId === requestId ? null : o));
    showToast(action === "approve"
      ? ct("toast.device_approved", "Device approved")
      : ct("toast.device_declined", "Link cancelled"), action === "approve" ? "good" : "bad");
  }

  const [deviceLabels, setDeviceLabels] = useState(null);
  useEffect(() => {
    let alive = true;
    apiGetDevices(code).then((d) => { if (alive) setDeviceLabels(d || {}); }).catch(() => {});
    return () => { alive = false; };
  }, [code]);
  // LB-343 (G2): every entry names the ADULT who did it, not the phone it was done on — on a
  // shared family phone those are different questions, and "who" is the one a parent is
  // asking. The fallback chain is the brief's: the name written at the time, then the adult
  // the row names, then the owner of the device it was written on, then "A parent". The
  // device's own label is no longer the answer here; Settings -> Devices is where a phone is
  // identified.
  function txnViaLabel(t) {
    if (!t.deviceId) return undefined;
    if (t.deviceId === "system") return ct("activity.via_system", "Money Monday");
    const who = attributionName(state, t, deviceOwnerMap(deviceLabels));
    if (who) return who;
    // LB-343 replaced "which device" with "which person": attributionName above answers it,
    // and this is the answer when the record names nobody. Everything that used to follow —
    // the device-label chain, the shortLabel trim, the removed-device string — was left
    // behind that return, unreachable, when the person-first answer landed. Deleted rather
    // than kept: it cannot run, and a reader who finds it has to work out which of the two
    // schemes is live. (activity.via_removed is still real copy — KidDetail asks for it on a
    // path that IS reachable, and its seed row stays.)
    return ct("activity.by_parent", "A parent");
  }
  // Which Settings category to open on. Lives here rather than inside SettingsTab so another
  // tab can deep-link into one — the Requests tab's "no one is being notified" banner jumps
  // straight to Notifications rather than dropping the parent at the Settings menu to hunt.
  const [settingsCategory, setSettingsCategory] = useState(null);
  // True only when Settings was entered via a deep link (the household-money row on Home,
  // or Requests' notifications banner) rather than the header's ⚙️ gear — lets the
  // category screen's own back button skip the settings menu and exit straight back to
  // where the parent actually came from, in one tap, instead of leaving them to find the
  // small "‹ Back" above it. Reset the moment Settings is actually left, so a later
  // gear-icon open never inherits a stale deep-link exit.
  const [settingsViaDeepLink, setSettingsViaDeepLink] = useState(false);
  // The tab to return to when leaving Settings — whichever parent tab was active right
  // before Settings opened, from EITHER entry path (gear icon or a deep link), so "‹ Back"
  // always genuinely goes back rather than assuming Home. A ref, not state: this only
  // needs to be read at the moment of the back tap, never drives a re-render itself.
  // (R-B restructure, Aug 2026: default + label map updated to the three new bottom-bar
  // tabs — adjust/learning are retired, see App.jsx's PARENT_TABS.)
  const returnTabRef = useRef("home");
  useEffect(() => {
    if (parentTab !== "settings") {
      returnTabRef.current = parentTab;
      setSettingsViaDeepLink(false);
    }
  }, [parentTab]);
  const RETURN_TAB_LABEL = { home: "Home", requests: "Requests", profiles: "Kids" };
  // LB-315: the layers this view opens over a tab. Back closes the topmost only.
  useBackLayer(leHubOpen, () => setLeHubOpen(false));
  useBackLayer(!!joinOverlay, () => setJoinOverlay(null));
  function exitSettings() {
    setSettingsCategory(null);
    setParentTab(returnTabRef.current || "home");
  }
  /* Accepting the rate-change rescale offer lands the parent on the Spending review
   * (Alan, 30 Aug 2026). Adjusting every reward in the household is exactly the moment the
   * question "so does this still fit their cap?" becomes live, and answering it on the page
   * built for it beats leaving them to go and look. With one child it goes straight there;
   * with several it opens the ordinary child picker first, same as the Home pill.
   */
  function goSpendingReview() {
    const kids = state.profiles || [];
    if (!kids.length) return;
    setSettingsCategory(null);
    if (kids.length === 1) { goKidSection(kids[0].id, "review", "settings"); return; }
    setParentTab("home");
    setPendingHomeSection("review");
  }

  function openSettingsDeepLink(category) {
    setSettingsCategory(category);
    setSettingsViaDeepLink(true);
    setParentTab("settings");
  }

  // R-C polish (Aug 2026, punch-list 5/6): kid-specific quick actions land on the Kids tab
  // with a target section to open (KidDetail's `focus` effect does the opening + the
  // controlled-open scroll). `from` powers the "‹ Back to {tab}" affordance — no History
  // API (swipe gestures are deferred by decision until the iOS-app wrapper); the nonce
  // makes repeating the same action re-trigger the effect. Cleared the moment the parent
  // navigates anywhere else themselves (the bottom bar / FAB all leave the Kids tab).
  const [kidFocus, setKidFocus] = useState(null);
  useEffect(() => {
    if (parentTab !== "profiles" && kidFocus) setKidFocus(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [parentTab]);
  // R-E (Aug 2026): Requests jumps from Home (the quick-action pill or the amber waiting
  // banner) get the same "‹ Back to {tab}" affordance as kid-detail jumps. Bottom-bar taps
  // never set this; leaving the Requests tab clears it.
  const [requestsFrom, setRequestsFrom] = useState(null);
  useEffect(() => {
    if (parentTab !== "requests" && requestsFrom) setRequestsFrom(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [parentTab]);
  // Set when something outside Home wants Home's child picker to open on arrival — the
  // picker lives in HomeTab, so this is the handoff rather than a second copy of it.
  const [pendingHomeSection, setPendingHomeSection] = useState(null);
  function goRequestsFromHome() { setRequestsFrom("home"); setParentTab("requests"); }
  function goKidSection(childId, section, from) {
    setActiveChildId(childId);
    setKidFocus({ section, from: from || parentTab, nonce: Date.now() });
    setParentTab("profiles");
  }

  // Same shape as goKidSection, but targets a collapsed ROW inside the kid detail rather
  // than one of the quick-action modals — see KidDetail's focus.row effect. Used by
  // Requests' over-cap "Adjust their cap →" link, which needs the cap row itself open, not
  // a modal. Carrying `from` also gives that jump the standard "‹ Back to Requests"
  // affordance, which it didn't have before.
  function goKidRow(childId, row, from) {
    setActiveChildId(childId);
    setKidFocus({ row, from: from || parentTab, nonce: Date.now() });
    setParentTab("profiles");
  }

  // (R-B restructure) The old no-child guard effect — which redirected away from tabs that
  // required an active child (the retired "adjust"/"learning" ids) — is gone: none of the
  // three new tabs can get stuck without one. Home degrades gracefully with an empty family
  // list, Requests doesn't need a child at all, and Profiles always renders (its own
  // unconditional "+ Add child profile" form is exactly how a household gets its first
  // child) even when state.profiles is empty.

  // unsavedGuardRef (Aug 2026): a ref App.jsx owns and reads from before switching Parent
  // Mode tabs or exiting to Kid Mode — see App.jsx's own comment on attemptNav for the full
  // picture. Kept pointed at ProfilesTab's imperative handle (hasUnsavedChanges/trySave/
  // discard, see that component's own top comment) only while the Kids tab is actually the
  // one showing; every other tab has nothing to guard, so it's explicitly cleared rather
  // than left stale pointing at an unmounted component. No dependency array — this needs to
  // re-run after every commit, not just when parentTab changes, because profilesTabRef.current
  // itself only becomes valid after React finishes committing ProfilesTab's mount/update,
  // which happens after this component's own render body has already run.
  const profilesTabRef = useRef(null);
  useEffect(() => {
    if (!unsavedGuardRef) return;
    unsavedGuardRef.current = parentTab === "profiles"
      ? {
          // Forwarders, not a copied handle: ProfilesTab re-creates its imperative handle
          // (fresh childDraft closures) on every one of ITS renders, and those renders
          // (typing in the edit form) don't re-commit ParentView — so a copied pointer
          // goes stale and answers hasUnsavedChanges() from a null-draft closure.
          // Dereferencing profilesTabRef.current at call time always reaches the live one.
          hasUnsavedChanges: () => profilesTabRef.current?.hasUnsavedChanges?.() ?? false,
          trySave: () => profilesTabRef.current?.trySave?.() ?? true,
          discard: () => profilesTabRef.current?.discard?.(),
        }
      : null;
  });

  // Reveals ProfilesTab's own (unconditionally-rendered) "+ Add child profile" form — the
  // Kids tab's "+" add-child chip (R-B addendum, Resolution 4) just needs to land a parent on
  // that tab; the form itself needs no new plumbing to be found once there.
  function goAddChild() {
    setParentTab("profiles");
    // Already on Kids: the tab switch is a no-op, so open the form directly. From another
    // tab, ProfilesTab is not mounted yet — give it a tick to appear, then open.
    const open = () => profilesTabRef.current?.openAddChild?.();
    if (parentTab === "profiles") open(); else setTimeout(open, 50);
  }
  // LB-129: the child just added from the Kids tab, while their setup sheet is open.
  const [setupChildId, setSetupChildId] = useState(null);
  const setupChild = setupChildId ? state.profiles.find((p) => p.id === setupChildId) : null;

  return (
    <div className="pop">
      {parentTab === "settings" ? (
        <>
          {/* R-C polish (Aug 2026, punch-list 3): exactly ONE back control per Settings
              screen. Inside a category, SettingsCategoryDetail renders its own back (to the
              category list), so this outer "back to the app" button hides; it returns at
              the category list, where it's the only one. */}
          {!settingsCategory && (
            <button className="text-xs font-bold text-slate-500 mb-3" onClick={exitSettings}><Icon id="ic-back" size={12} className="inline-block align-[-1px] mr-1" style={{ color: "currentColor" }} />{ct("settings.back_to", "Back to {tab}", { tab: RETURN_TAB_LABEL[returnTabRef.current] || "Home" })}</button>
          )}
          <SettingsTab onAwaitingJoin={setAwaitingJoin} state={state} update={update} showToast={showToast} onSpendingReview={goSpendingReview} code={code} verifiedPin={verifiedPin} setPendingVerifiedPin={setPendingVerifiedPin} setPendingVerifiedPasscode={setPendingVerifiedPasscode} setPendingVerifiedJoinToken={setPendingVerifiedJoinToken} onLeaveHousehold={onLeaveHousehold} initialCategory={settingsCategory} onCategoryChange={setSettingsCategory} viaDeepLink={settingsViaDeepLink} onExitSettings={exitSettings} />
        </>
      ) : (
        <>
          {/* R-D (Aug 2026, punch-list 2): the prototype's page heading — "Parent Mode" with
              ⚙️ Settings to its RIGHT — lives here above the tab content on all three tabs,
              not in the app header. The button keeps aria-label="Settings" so every existing
              accessible-name spec match is untouched by the visible text. */}
          {/* Design pass A (Sep 2026): the August marigold wash behind this row is gone —
              gradients are confined to the kid hero theme slot (docs/DESIGN_GUIDANCE.md §3);
              the heading is type on the canvas. Markup and names unchanged. */}
          <div className="flex items-end justify-between px-1 py-1 mb-2">
            {/* LB-229 (8 Sep 2026, option A on Alan's sign-off): the heading names the tab, with
                "Parent Mode" kept as a caption above it — a section label, not a third layer
                of chrome. On Requests the old heading contradicted the tab bar. */}
            <div>
              <div className="text-xs font-medium text-slate-500 leading-tight">{ct("parent.heading", "Parent Mode")}</div>
              <h2 className="text-xl font-extrabold text-slate-900 leading-tight">{parentTab === "requests" ? ct("parent.heading_requests", "Requests") : parentTab === "profiles" ? ct("parent.heading_kids", "Kids") : ct("parent.heading_home", "Home")}</h2>
            </div>
            <button onClick={onOpenSettings} aria-label="Settings" title="Settings" className="bg-pill text-navy rounded-lg px-3 py-1.5 text-sm font-semibold whitespace-nowrap flex-shrink-0"><Icon id="ic-gear" size={16} className="inline-block align-[-3px] mr-1.5" style={{ color: "currentColor" }} />Settings</button>
          </div>
          {parentTab === "home" && leHubOpen && (
            <LearnEarnHub state={state} update={update} showToast={showToast} startOnSetHomework={leHubSetHomework} onClose={() => { setLeHubOpen(false); setLeHubSetHomework(null); }} />
          )}
          {parentTab === "home" && !leHubOpen && (
            <HomeTab
              txnViaLabel={txnViaLabel}
              state={state} update={update} code={code} showToast={showToast}
              activeChild={activeChild}
              setActiveChildId={setActiveChildId}
              setParentTab={setParentTab}
              goKidSection={goKidSection} pendingSection={pendingHomeSection} onPendingSectionHandled={() => setPendingHomeSection(null)}
              onOpenRequests={goRequestsFromHome}
              onOpenHouseholdMoney={() => openSettingsDeepLink("rewards")}
              onOpenLearnEarnHub={() => { setLeHubSetHomework(null); setLeHubOpen(true); }}
              onOpenSetHomework={(childId) => { setLeHubSetHomework(childId); setLeHubOpen(true); }}
            />
          )}
          {parentTab === "requests" && requestsFrom && (
            <button className="text-xs font-bold text-slate-500 self-start mb-2" onClick={() => { const to = requestsFrom; setRequestsFrom(null); setParentTab(to); }}>
              <Icon id="ic-back" size={12} className="inline-block align-[-1px] mr-1" style={{ color: "currentColor" }} />{ct("settings.back_to", "Back to {tab}", { tab: RETURN_TAB_LABEL[requestsFrom] || "Home" })}
            </button>
          )}
          {parentTab === "requests" && <RequestsTab focusRequest={requestsFocus} onFocusHandled={onRequestsFocusHandled} state={state} update={update} showToast={showToast} onApproveHomework={onApproveHomework} onRejectHomework={onRejectHomework} addTransaction={addTransaction} moveTokensToVault={moveTokensToVault} reverseTransaction={reverseTransaction} reverseRequest={reverseRequest} rejectVaultTransfer={rejectVaultTransfer} code={code} onOpenNotificationSettings={() => openSettingsDeepLink("notifications")} onAdjustCap={(childId) => goKidRow(childId, "cap", "requests")} joinRequests={joinRequests} joinBusy={joinBusy} onActOnJoinRequest={actOnJoinRequest} />}
          {parentTab === "profiles" && (
            <div className="flex flex-col gap-2">
              {kidFocus?.from && kidFocus.from !== "profiles" && (
                <button className="text-xs font-bold text-slate-500 self-start" onClick={() => { const to = kidFocus.from; setKidFocus(null); setParentTab(to); }}>
                  <Icon id="ic-back" size={12} className="inline-block align-[-1px] mr-1" style={{ color: "currentColor" }} />{ct("settings.back_to", "Back to {tab}", { tab: RETURN_TAB_LABEL[kidFocus.from] || "Home" })}
                </button>
              )}
              {/* R-D (Aug 2026, punch-list 5): the design's side-by-side child pills replace
                  the pill+sheet switcher HERE ONLY — Kid Mode's own switcher is untouched.
                  KidChips since 3 Oct, shared with Earnings check (which has no "+"). */}
              <KidChips profiles={state.profiles} activeId={activeChild?.id} onSelect={setActiveChildId} hiddenIds={hiddenIds} onAdd={goAddChild} />
              <ProfilesTab ref={profilesTabRef} state={state} update={update} updateAndSave={updateAndSave} code={code} showToast={showToast} setActiveChildId={setActiveChildId} addTransaction={addTransaction} hiddenIds={hiddenIds} onToggleVisibility={onToggleVisibility} mutedIds={mutedIds} onToggleMute={onToggleMute} onOpenHouseholdMoney={() => openSettingsDeepLink("rewards")} singleChildId={activeChild?.id} onChildAdded={setSetupChildId} />
              {setupChild && (
                <NewChildSetupSheet child={setupChild} state={state} update={update} showToast={showToast} onDone={() => setSetupChildId(null)} />
              )}
              {activeChild && (
                <KidDetail child={activeChild} onSelectChild={setActiveChildId} hiddenIds={hiddenIds} state={state} update={update} addTransaction={addTransaction} reverseTransaction={reverseTransaction} code={code} showToast={showToast} focus={kidFocus} onFocusModalClosed={() => { const to = kidFocus?.from || "home"; setKidFocus(null); setParentTab(to); }} />
              )}
            </div>
          )}
        </>
      )}
      {/* J2 — the overlay, only when a request arrives with the app already open. Not a
          route and not a tab: it is the app saying "this is happening now", and the row
          in Requests below is what remains when it is dismissed.
          LB-341 (22 Sep): it sits OUTSIDE the settings/non-settings ternary above, and must
          stay there. It was authored inside the else branch, so a parent sitting in Settings
          — which is exactly where "Add a device" shows them the QR they just scanned — got
          no overlay at all, and after #562 removed the inline card there was no approval
          surface on that screen. The overlay is the only surface, so it renders on every
          parent screen. scripts/check-settings-surfaces.mjs holds this from Settings. */}
      {joinOverlay && (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4" role="dialog" aria-modal="true" aria-label={ct("join.overlay_title", "A device wants to join")}>
          <div className="bg-white rounded-2xl p-5 max-w-xs w-full text-center shadow-xl" data-testid="join-request-overlay">
            <div className="text-lg font-extrabold text-slate-800 mb-1">{ct("join.overlay_title", "A device wants to join")}</div>
            <div className="text-sm text-slate-600 mb-3">{ct("join.overlay_body", "Someone opened a link to join your household.")}</div>
            <div className="bg-slate-50 border border-slate-100 rounded-lg divide-y divide-slate-200 mb-3 text-left">
              <div className="flex px-3 py-2"><span className="flex-1 text-xs font-semibold text-slate-700">{ct("join.overlay_device", "Device")}</span><span className="text-xs text-slate-500 font-medium truncate">{joinOverlay.label || ct("join.overlay_unknown_device", "A device")}</span></div>
              {joinOverlay.where && <div className="flex px-3 py-2"><span className="flex-1 text-xs font-semibold text-slate-700">{ct("join.overlay_where", "Where")}</span><span className="text-xs text-slate-500 font-medium truncate">{joinOverlay.where}</span></div>}
              <div className="flex px-3 py-2"><span className="flex-1 text-xs font-semibold text-slate-700">{ct("join.overlay_sent_by", "Link sent by")}</span><span className="text-xs text-slate-500 font-medium truncate">{joinOverlay.mine ? ct("join.overlay_sent_by_you", "You") : ct("join.overlay_sent_by_other", "Another adult")}</span></div>
            </div>
            <button type="button" data-testid="join-overlay-approve" disabled={joinBusy === joinOverlay.requestId} className="w-full bg-slate-900 text-white font-bold text-sm rounded-xl py-3 mb-2 disabled:opacity-50" onClick={() => actOnJoinRequest(joinOverlay.requestId, "approve")}>{ct("join.overlay_approve", "Approve this device")}</button>
            <button type="button" data-testid="join-overlay-cancel" disabled={joinBusy === joinOverlay.requestId} className="w-full text-rose-700 font-bold text-sm py-2 disabled:opacity-50" onClick={() => actOnJoinRequest(joinOverlay.requestId, "cancel")}>{ct("join.overlay_reject", "Not me. Cancel the link")}</button>
            <button type="button" className="w-full text-slate-500 font-bold text-sm pt-2" onClick={() => setJoinOverlay(null)}>{ct("join.overlay_later", "Decide later")}</button>
          </div>
        </div>
      )}
    </div>
  );
}

/* -------------------------------- Home tab -------------------------------- */
// Greenfield surface (light brief, build-safeguards-component-register.md's two-tier
// gating) — dashboard + quick actions per the redesign prototype's HomeTab. Till/Add/choice-
// screen machinery doesn't exist yet (brief §7(6)), so the quick actions that would drive
// those in the prototype route to today's equivalent instead: Manage balances, inside the
// Kids tab's "Do now" group (see KidDetail.jsx).
// txnViaLabel arrives as a PROP from ParentView (which owns the device-labels fetch) —
// PR(iii) originally defined it in ParentView but referenced it from in here, a free
// variable esbuild can't flag; the first-ever expansion of "Household activity"
// (spec 41) white-screened on the ReferenceError. One fetch, passed down.
// Exported so the CMS screen list can name it as a screen root, as InstallExplainerCard is.
export function HomeTab({ state, update, code, showToast, activeChild, setActiveChildId, setParentTab, goKidSection, onOpenRequests, onOpenHouseholdMoney, txnViaLabel, pendingSection, onPendingSectionHandled, onOpenLearnEarnHub, onOpenSetHomework = () => {} }) {
  const profiles = state.profiles || [];
  const pending = (state.requests || []).filter((r) => r.status === "pending").length;
  const rate = state.settings?.exchangeRate > 0 ? state.settings.exchangeRate : 10;
  const interestOn = !!state.settings?.vaultInterest?.enabled;
  const interestApr = clampApr(state.settings?.vaultInterest?.apr);
  const recent = (state.transactions || []).slice(0, 6);

  function goRequests() { onOpenRequests(); }
  function goKid(childId) { setActiveChildId(childId); setParentTab("profiles"); }
  // R-C polish (Aug 2026, punch-list 5): kid-specific quick actions ask WHICH kid first —
  // but only when the household actually has more than one child in total (visibility is a
  // kid-mode device concept and deliberately plays no part here, per Alan). One child skips
  // the sheet; zero children just lands on the Kids tab, whose add-child form is the answer.
  const [picker, setPicker] = useState(null); // { section }
  useBackLayer(!!picker, () => setPicker(null)); // LB-315
  // Arriving from somewhere that already decided WHAT the parent wants but not FOR WHOM —
  // SettingsTab's onSpendingReview. Its only caller was the LB-83 rate-rescale offer, which
  // LB-355 replaced with the three-step change; kept as the route for the next caller.
  // Opens this tab's own picker rather than
  // duplicating it, and clears the handoff so a later tab switch doesn't reopen it.
  useEffect(() => {
    if (!pendingSection) return;
    kidAction(pendingSection);
    if (onPendingSectionHandled) onPendingSectionHandled();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pendingSection]);
  function kidAction(section) {
    if (profiles.length > 1) { setPicker({ section }); return; }
    if (profiles.length === 1) { goSection(profiles[0].id, section); return; }
    setParentTab("profiles");
  }
  // "homework" is not a section of the child's page: it is Set homework inside the Learn & Earn
  // hub (Alan, 2 Oct). Same child rule as every other per-child tile; a different destination.
  function goSection(childId, section) {
    if (section === "homework") { onOpenSetHomework(childId); return; }
    goKidSection(childId, section, "home");
  }

  return (
    <div className="flex flex-col gap-4">
      {/* LB-164 parent Home (pass I): a waiting request is the ask, so it is the one action
          card at the top — who is waiting, and Review. With nothing waiting it is a quiet row
          so "Open requests" stays reachable from Home (same aria-label as the old tile). */}
      {pending > 0 ? (
        <div className="bg-white rounded-lg shadow-lg border-l-[3px] border-indigo-500 px-4 py-3 flex items-center gap-3" data-testid="home-requests-card">
          <span className="w-9 h-9 rounded-full bg-indigo-50 flex items-center justify-center flex-shrink-0"><Icon id="ic-requests" size={18} hue="indigo" /></span>
          <div className="flex-1 min-w-0">
            <div className="text-sm font-semibold text-slate-900">{ct("home.requests_waiting", "{count} request{plural} waiting for you", { count: pending, plural: pending > 1 ? "s" : "" })}</div>
            <div className="text-xs text-slate-500 truncate">{[...new Set((state.requests || []).filter((r) => r.status === "pending").map((r) => profiles.find((p) => p.id === r.childId)?.name).filter(Boolean))].join(" · ")}</div>
          </div>
          <button onClick={goRequests} aria-label="Open requests" className="bg-slate-900 text-white text-sm font-bold rounded-lg px-4 py-2 flex-shrink-0">{ct("home.requests_review", "Review")}</button>
        </div>
      ) : (
        <button onClick={goRequests} aria-label="Open requests" className="w-full flex items-center justify-between px-1 py-1 text-sm">
          <span className="flex items-center gap-2 text-slate-700"><Icon id="ic-requests" size={16} hue="indigo" />{ct("home.action_requests", "Requests")} <span className="text-slate-400">· {ct("home.action_requests_sub_clear", "All clear")}</span></span>
          <Icon id="ic-forward" size={16} className="text-slate-400 flex-shrink-0" style={{ color: "currentColor" }} />
        </button>
      )}

      {/* 12 Sep (Alan): the weekly summary sits with the request notifications at the top,
          not below Family at a glance — both are "here is what happened, look at it". */}
        {coachEnabled(state.settings) && (() => {
          const wk = weekStartOf(todayStr());
          let dismissed = false; try { dismissed = localStorage.getItem("lb_adaptive_week_dismissed") === wk; } catch {}
          if (dismissed) return null;
          // Alan, 15 Sep: a DIGEST of Monday-to-Sunday just gone, not a rolling last-seven-days.
          // Same numbers every time it is opened until dismissed or next Monday replaces it.
          const bounds = lastWeekBounds(todayStr());
          const lines = (state.profiles || []).map((c) => digestFor(c, (state.learningContent || {})[c.id], bounds)).filter(Boolean);
          if (!lines.length) return null;
          return (
            <div className="bg-white rounded-lg border border-slate-200 border-l-4 border-l-indigo-400 px-4 py-3 mb-4" data-testid="adaptive-week">
              <div className="flex items-start gap-2">
                <div className="min-w-0 flex-1">
                  <div className="text-sm font-semibold text-slate-900 flex items-center gap-2">{ct("adaptive.week.title", "Last week in Learn & Earn")}<span className="text-[10px] font-extrabold uppercase tracking-wide bg-amber-100 text-amber-800 rounded px-1.5 py-0.5">{ct("adaptive.row.premium", "Premium")}</span></div>
                  <div className="text-xs text-slate-400 mt-0.5 mb-1" data-testid="adaptive-week-range">{digestRangeLabel(bounds)}</div>
                  {lines.map((l) => <p key={l.name} className="text-sm text-slate-600 mt-0.5">{l.text}</p>)}
                </div>
                <button type="button" aria-label={ct("adaptive.week.dismiss", "Hide this week")} className="text-slate-400 text-lg leading-none px-1" onClick={(e) => { try { localStorage.setItem("lb_adaptive_week_dismissed", wk); } catch {} e.currentTarget.closest('[data-testid="adaptive-week"]').style.display = "none"; }}>×</button>
              </div>
            </div>
          );
        })()}
      <div >
        <div className="text-sm font-semibold text-slate-900 px-1 mb-2">{ct("home.quick_actions", "Quick actions")}</div>
        <div className="grid grid-cols-2 gap-2">
          {/* R-E (Aug 2026): hues follow the same palette semantics as the Kids pills —
              gold for money/earning, indigo for learning; Requests goes amber ONLY while
              something is actually waiting (amber = attention, never decorative). */}
          <HomeQuickAction icon="ic-till" hue="gold" label={ct("home.action_spend", "Record a spend")} sub={ct("home.action_spend_sub", "Tokens spent on something real")} onClick={() => { track("quick_action", { kind: "spend" }); kidAction("till"); }} />
          <HomeQuickAction icon="ic-banknote" hue="gold" label={ct("home.action_add", "Give money")} sub={ct("home.action_add_sub", "Tokens, or pounds into the vault")} onClick={() => { track("quick_action", { kind: "add" }); kidAction("add"); }} />
          {/* Order (Alan, 30 Aug 2026): now that labels wrap, the two long ones — Household
              money and Spending review — are the tiles that grow to two lines, so they are
              paired on the SAME row. Grid rows size to their tallest cell, so keeping the
              deep pair together means each row's two tiles match each other and only the
              last row is taller, instead of two ragged rows. Nothing else about these
              pills changed: same ids, labels, aria-labels and handlers.
              2 Oct (Alan): Spending review left the grid for its own row under Household money
              (now named Earnings check) and Set homework took its place; Learn & Earn and Set
              homework, both indigo, now share the second row. */}
          <HomeQuickAction icon="ic-brain" hue="indigo" label={ct("home.action_manage_learning", "Learn & Earn")} sub={ct("home.action_manage_learning_sub", "Learning progress and settings")} onClick={() => { track("quick_action", { kind: "learnearn" }); onOpenLearnEarnHub(); }} />
          {/* Set homework (Alan, 2 Oct 2026). Spellings come home every week, and setting them
              was three taps deep (Learn & Earn, Homework practice, Set homework); the tile goes
              to the thing done most often. Same child rule as the other per-child tiles (the
              picker with more than one child, straight through with one, the Kids tab with
              none), ending on step one of Set homework for that child: kidAction("homework"),
              which goSection hands to the Learn & Earn hub. Indigo: learning, like the tile
              beside it. This took the place of Spending review (30 Aug), which is now the
              Earnings check row under Household money below. */}
          <HomeQuickAction icon="ic-spelling" hue="indigo" label={ct("home.action_homework", "Set homework")} sub={ct("home.action_homework_sub", "Spellings, vocabulary or a language")} onClick={() => { track("quick_action", { kind: "homework" }); kidAction("homework"); }} />
        </div>
        {/* R-D (Aug 2026, punch-list 3+6): the picker is now a bottom sheet — avatar chip,
            name and balances per child, teal-accent header — and rendered through a PORTAL
            to document.body, escaping ParentView's `.pop` transform animation (the same
            fixed-inside-animated-ancestor bug class the KidDetail focus windows left). */}
        {/* Backdrop is NOT a dismiss target (Alan, 28 Aug) — the Cancel row at the foot
            of the sheet is the only exit. */}
        {picker && ReactDOM.createPortal(
          <div className="fixed inset-0 z-40 bg-black/40 flex items-end sm:items-center justify-center" role="dialog" aria-label="Choose a child">
            <div className="bg-white rounded-t-3xl sm:rounded-2xl p-5 w-full max-w-md sm:mx-4" style={{ paddingBottom: "calc(env(safe-area-inset-bottom, 0px) + 1.25rem)" }}>
              <div className="mx-auto w-10 h-1 rounded-full bg-slate-200 mb-3 sm:hidden" aria-hidden="true" />
              <div className="text-base font-extrabold text-slate-900 mb-3">{ct("home.picker_title", "Which child?")}</div>
              <div className="flex flex-col gap-2">
                {profiles.map((p, i) => (
                  <button
                    key={p.id}
                    className="flex items-center gap-3 p-3 rounded-2xl border border-slate-100 bg-slate-50 text-left active:bg-slate-100"
                    onClick={() => { const sec = picker.section; setPicker(null); goSection(p.id, sec); }}
                  >
                    <span className={`w-10 h-10 rounded-full flex items-center justify-center text-white font-extrabold text-base flex-shrink-0 ${i % 2 === 0 ? "bg-teal-500" : "bg-indigo-500"}`} aria-hidden="true">{(p.name || "?").slice(0, 1).toUpperCase()}</span>
                    <span className="flex-1 min-w-0">
                      <span className="block font-semibold text-slate-900 text-sm truncate">{p.name}</span>
                      <span className="text-xs text-slate-500 flex items-center gap-1"><CoinIcon className="w-3 h-3" />{p.tokenBalance} · <VaultIcon className="w-3 h-3" />{fmtPence(p.vaultBalancePence)}</span>
                    </span>
                    <Icon id="ic-forward" size={18} className="text-slate-300 flex-shrink-0" aria-hidden="true" style={{ color: "currentColor" }} />
                  </button>
                ))}
              </div>
              <button className="w-full mt-3 text-sm font-bold text-slate-500 py-2.5 rounded-xl border border-slate-200" onClick={() => setPicker(null)}>{ct("home.picker_cancel", "Cancel")}</button>
            </div>
          </div>,
          document.body
        )}
      </div>


      {/* Household money — the one household-scoped row on Home (redesign-feature-audit.md:
          "Household money" group = Rewards & Currency). Moved here verbatim from the old
          AdjustTab dark-slate banner — no duplicate mount left inside the Kids tab.
          Position (Alan, Aug 2026): sits ABOVE Family at a glance — the dark slate pill
          breaks up the run of white cards, and household-level money context reads
          naturally before the per-child rows. */}
      <div >
        <button type="button" onClick={onOpenHouseholdMoney} className="w-full text-left flex items-center justify-between px-1 mb-1">
          <span className="text-sm font-semibold text-slate-900">{ct("household_money.row_label", "Household money for all children")}</span>
          <span className="text-xs font-medium text-slate-500">{ct("household_money.change", "Change")}</span>
        </button>
        <div className="bg-white rounded-lg border border-slate-200 divide-y divide-slate-200">
          {(() => {
            const perTokenPrice = fmtGBP(1, rate);
            const interestText = interestOn
              ? ct("household_money.row_interest_on", "Vault interest {apr}% a year", { apr: interestApr })
              : ct("household_money.row_interest_off", "Vault interest off");
            // One row for the card. The money rows open the household settings; the Earnings check
            // row (2 Oct) passes its own handler, label, test id and a muted second line.
            const Row = ({ icon, hue, children, sub = null, onClick = onOpenHouseholdMoney, ariaLabel, testId }) => (
              <button type="button" onClick={onClick} aria-label={ariaLabel} data-testid={testId} className="w-full flex items-center gap-3 px-4 py-3 text-left active:bg-slate-50">
                <span className="w-7 h-7 rounded-full bg-slate-100 flex items-center justify-center flex-shrink-0"><Icon id={icon} size={16} hue={hue} /></span>
                <span className="flex-1 min-w-0 text-sm text-slate-900">{children}{sub && <span className="block text-xs text-slate-500">{sub}</span>}</span>
                <Icon id="ic-forward" size={16} className="text-slate-400 flex-shrink-0" style={{ color: "currentColor" }} />
              </button>
            );
            return (
              <>
                <Row icon="ic-exchange" hue="gold">{ct("household_money.row_rate", "{rate} tokens = {symbol}1 / 1 token = {price}", { rate, symbol: CURRENCY_SYMBOL, price: perTokenPrice })}</Row>
                <Row icon="ic-supersaver" hue="teal">{interestText}</Row>
                {/* Earnings check (was Spending review; renamed by Alan, 2 Oct: "Spending review"
                    read as a review of what the CHILD had spent, and the screen is about what they
                    can EARN against their weekly cap). 30 Aug it took a quick-action tile, because
                    "have my reward amounts drifted past the cap?" goes unasked until a child is
                    blocked mid-week. 2 Oct (Alan): the tile went to the weekly job, Set homework,
                    and this became its own row beneath this card; the same day, having seen it on
                    his phone, he joined it to the card ("Connect spending review to household
                    money. Mirror the formatting."), so it is the third row here, drawn by the same
                    Row. It is the only row in the card that asks which child (kidAction("review"):
                    the picker first with more than one), which is expected; the heading's Change
                    still opens the household money settings and nothing else. ariaLabel: the tile
                    carried "Open spending review" so its name did not start with "Spend" and
                    collide with Record a spend in the specs; the row keeps one stable name. */}
                <Row icon="ic-cap" hue="gold" ariaLabel="Open earnings check" testId="home-earnings-check" onClick={() => { track("quick_action", { kind: "review" }); kidAction("review"); }} sub={ct("home.action_spending_review_sub", "Manage your spending on rewards")}>{ct("home.action_spending_review", "Earnings check")}</Row>
              </>
            );
          })()}
        </div>
      </div>

      <div >
        {/* Adaptive, this week (LB-19; Alan 10 Sep: weekly, dismissable, only when a child did
            something). Same text as the Monday push — both read src/lib/weeklyDigest.js. Dismiss hides it for
            the rest of the week on this device. */}

        <div className="text-sm font-semibold text-slate-900 px-1 mb-2">{ct("home.family_glance", "Family at a glance")}</div>
        {profiles.length === 0 ? (
          <div className="bg-white rounded-2xl p-6 text-center text-sm text-slate-400 shadow-sm border border-slate-100">{ct("home.no_children", "Add a child profile to get started.")}</div>
        ) : (
          <div className="bg-white rounded-lg border border-slate-200 divide-y divide-slate-200">
            {profiles.map((p) => (
              <button
                key={p.id}
                onClick={() => goKid(p.id)}
                className="w-full flex items-center gap-3 px-4 py-3 text-left active:bg-slate-50"
              >
                <ChildMark profile={p} size="sm" />
                <div className="flex-1 min-w-0">
                  <div className="font-semibold text-slate-900 text-sm">{p.name}</div>
                  <div className="text-xs text-slate-500 flex gap-3 mt-0.5">
                    {/* R-H (Alan, prod device pass): emoji → the app's canonical money marks,
                        same as kid mode (money representations stay consistent). */}
                    <span className="inline-flex items-center gap-1"><CoinIcon className="w-3.5 h-3.5" />{p.tokenBalance}</span>
                    <span className="inline-flex items-center gap-1"><VaultIcon className="w-3.5 h-3.5" />{fmtPence(p.vaultBalancePence)}</span>
                  </div>
                </div>
                <span className="text-xs font-semibold text-slate-900 flex-shrink-0">{ct("home.glance_manage", "Manage")}</span>
                <Icon id="ic-forward" size={18} className="text-slate-900 flex-shrink-0" style={{ color: "currentColor" }} />
              </button>
            ))}
          </div>
        )}
      </div>


      {/* LB-370 (Alan, 24 Sep; canvas signed off): Invite a friend, directly under Family at a
          glance. Shows once a child has used the app (src/lib/referral.js childHasUsedApp) and
          goes the first time this household shares or copies its link; Settings → Household
          keeps it permanently. No dismiss cross, by decision. Same card grammar as the
          household-money rows: white, hairline, one filled button. */}
      {showReferralHomeCard(state) && (
        <div className="bg-white rounded-lg border border-slate-200 px-4 py-3 flex items-center gap-3" data-testid="home-referral-card">
          <span className="w-9 h-9 rounded-full flex items-center justify-center flex-shrink-0" style={{ background: QUICK_ACTION_CHIP_TINTS.gold }} aria-hidden="true"><Icon id="ic-sent" size={18} hue="gold" /></span>
          <div className="flex-1 min-w-0">
            <div className="text-sm font-semibold text-slate-900">{ct("home.referral.title", "Invite a friend")}</div>
            <div className="text-xs text-slate-500">{ct("home.referral.body", "Know a family who would like this? Share your link.")}</div>
          </div>
          <button type="button" className="bg-slate-900 text-white text-sm font-bold rounded-lg px-4 py-2 flex-shrink-0" data-testid="home-referral-share" onClick={async () => {
            track("quick_action", { kind: "referral" });
            const out = await shareReferral(code, { mode: "share" });
            if (out === "shared" || out === "copied") {
              markReferralShared(update);
              if (out === "copied") showToast(ct("toast.link_copied", "Link copied"), "good");
            } else if (out && out.error) {
              showToast(ct("settings.referral.err_generic", "Couldn't create your link just now"), "bad");
            }
          }}>{ct("home.referral.share", "Share")}</button>
        </div>
      )}

      {recent.length > 0 && (
        <div >
          <div className="text-sm font-semibold text-slate-900 px-1 mb-2">{ct("home.recent_activity", "Recent household activity")}</div>
          <div className="bg-white rounded-lg border border-slate-200 px-4 py-1" data-testid="home-household-activity">
            <ActivityList
              items={recent}
              emptyText="No transactions yet."
              renderRow={(t) => {
                const style = activityRowStyle(t);
                const child = profiles.find((p) => p.id === t.childId);
                return (
                  <ActivityRow
                    key={t.id}
                    {...style}
                    label={child ? `${child.name} · ${t.reason}` : t.reason}
                    viaLabel={txnViaLabel(t)}
                    timestamp={fmtActivityStamp(t.timestamp)}
                    status={t.status}
                    reversed={t.reversed}
                    isReversal={!!t.reversalOf}
                  />
                );
              }}
            />
          </div>
        </div>
      )}
    </div>
  );
}

// R-C polish (Aug 2026, punch-list 9 + 10): one line shorter — the sprite icon sits LEFT of
// the text block instead of on its own line — and the icon comes from the tintable sprite
// (docs/ui-icons.md ids) via the shared Icon component, not emoji.
// Brand-warm pass (idea 3): a ~10%-opacity circle in the icon's own hue behind each
// quick-action icon — the action grid becomes the page's colour anchor without any copy
// or accessible-name change (icons stay aria-hidden). Tints match docs/ui-icons.md hues.
const QUICK_ACTION_CHIP_TINTS = {
  gold: "rgba(224,168,28,0.12)",
  indigo: "rgba(91,91,214,0.10)",
  amber: "rgba(217,119,6,0.12)",
  teal: "rgba(15,155,142,0.10)",
  pink: "rgba(232,106,146,0.10)",
};

function HomeQuickAction({ icon, hue, label, sub, onClick, badge, ariaLabel }) {
  return (
    <button
      onClick={onClick}
      aria-label={ariaLabel}
      className="relative bg-white rounded-lg shadow-lg px-3 py-2.5 text-left active:scale-[0.98] transition-transform flex items-center gap-2.5"
    >
      {badge > 0 && (
        <span className="absolute top-1.5 right-1.5 bg-rose-500 text-white text-xs font-bold rounded min-w-[18px] h-[18px] px-1 flex items-center justify-center">
          {badge}
        </span>
      )}
      <span className="w-9 h-9 rounded-full flex items-center justify-center flex-shrink-0" style={{ background: QUICK_ACTION_CHIP_TINTS[hue] || "rgba(90,100,120,0.08)" }} aria-hidden="true">
        <Icon id={icon} hue={hue} size={20} />
      </span>
      {/* Alan, 30 Aug 2026: these used to `truncate`, so "Household money" read
          "Household m..." and "Spending review" read "Spending revi..." — a label a parent
          can't finish reading is worse than a taller tile. They wrap now instead. Wrapping
          rather than shortening the copy on purpose: every string here comes through ct()
          and can be rewritten in the Content Studio at any time, so a layout that only fits
          today's exact wording would truncate again the first time someone edits it.
          break-words covers a single long word (a translation, a renamed action) that can't
          fit the column at all. */}
      <span className="min-w-0">
        <span className="font-semibold text-slate-900 text-sm block leading-tight break-words">{label}</span>
        <span className="text-xs text-slate-500 block leading-snug break-words mt-0.5">{sub}</span>
      </span>
    </button>
  );
}


/* ----------------------------- Requests tab ------------------------------- */
